import { use, useId, useState } from "react";
import { useNavigate } from "react-router";
import type { ReviewGate, RunState } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { HoverTip } from "@/components/ui/HoverTip";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { SlideToConfirm } from "@/components/ui/SlideToConfirm";
import {
  isRunActive,
  useLatestRunId,
  useLatestRunStanding,
  useStartRun,
} from "@/hooks/use-generate";
import { TerminologyContext } from "../../terms/Terminology";
import { ApiError, refusalDetail } from "@/services/api-client";
import {
  FIGURE_SUB_STEPS,
  INTAKE_SUB_STEPS,
  NOVELTY_TABS,
  THESIS_SUB_STEPS,
  findStage,
  openQuestionsPath,
  stagePath,
  subStepPath,
  tabPath,
} from "../../stage-vocabulary";
import { EditLink, type EditTarget } from "./ReviewCard";

function labelOf(steps: readonly { id: string; label: string }[], id: string) {
  return steps.find((step) => step.id === id)?.label ?? id;
}

/**
 * Where a shut gate is opened. The stage gate's criteria, and the invention's
 * settlement, are each met on one Thesis page, and the figure review on Review
 * Figures, so they lead to that page; every other gate leads to its stage.
 */
function whereOpened(taskId: string, gate: ReviewGate): EditTarget {
  switch (gate.gate) {
    case "element_confirmed":
    case "invention_settled":
      return {
        label: labelOf(THESIS_SUB_STEPS, "invention"),
        to: subStepPath(taskId, "thesis", "invention"),
      };
    case "position_confirmed":
    case "assessment_accepted": {
      const tab = gate.gate === "position_confirmed" ? "positions" : "analysis";
      return {
        label: labelOf(NOVELTY_TABS, tab),
        to: tabPath(taskId, "thesis", "novelty", tab),
      };
    }
    case "classification":
      return {
        label: labelOf(INTAKE_SUB_STEPS, "classification"),
        to: subStepPath(taskId, "intake", "classification"),
      };
    case "figures_reviewed":
      return {
        label: labelOf(FIGURE_SUB_STEPS, "review-insert"),
        to: subStepPath(taskId, "figures", "review-insert"),
      };
    case "questions_answered":
      return { label: "Open Questions", to: openQuestionsPath(taskId) };
    default:
      return {
        label: findStage(gate.stage)?.label ?? gate.stage,
        to: stagePath(taskId, gate.stage),
      };
  }
}

/** The gates still shut, each with the way to the stage that opens it. */
function StillToDo({ taskId, gates }: { taskId: string; gates: ReviewGate[] }) {
  const id = useId();
  return (
    <div className="mt-4">
      <MicroLabel as="h3" id={id} className="mb-2">
        Still to do before you generate
      </MicroLabel>
      <ul aria-labelledby={id} className="space-y-1.5 text-[13px]">
        {gates.map((gate) => (
          <li
            key={gate.gate}
            className="flex flex-wrap items-baseline justify-between gap-x-4"
          >
            <span>{gate.label}</span>
            <EditLink target={whereOpened(taskId, gate)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Where the start stands: a run under way, a start sent, the content locked by the slide, or
 * none of these. The slide, Generate and the words beside them are each read from it.
 */
type StartStatus = "generating" | "starting" | "locked" | "open";

/**
 * Generate, the one act that starts the draft. It is held, with its reason, until the slide has
 * confirmed the content, and while a run is starting or under way, since one runs at a time.
 */
function GenerateButton({
  status,
  onGenerate,
}: {
  status: StartStatus;
  onGenerate: () => void;
}) {
  const held = status !== "locked";
  return (
    <HoverTip
      tip={
        status === "generating"
          ? "A draft is being generated. One runs at a time."
          : status === "locked"
            ? "Starts the draft from the content you confirmed."
            : "Slide to confirm the content first."
      }
      wide={false}
    >
      {({ triggerProps }) => (
        <Button
          type="button"
          held={held}
          onClick={held ? undefined : onGenerate}
          {...triggerProps}
        >
          {status === "generating" ? "Generating…" : "Generate patent draft"}
        </Button>
      )}
    </HoverTip>
  );
}

/** What the page says beside the slide, in the attorney's words. */
function standingWords(
  standing: RunState | null | undefined,
  status: StartStatus,
  ready: boolean,
): { words: string; link: string | null } {
  if (status === "generating")
    return { words: "Your draft is being generated.", link: "See progress" };
  if (status === "starting")
    return { words: "Starting the draft…", link: null };
  if (status === "locked")
    return {
      words: "Content confirmed. Generate the draft when you are ready.",
      link: null,
    };
  if (standing === "ready")
    return { words: "Your last draft is ready.", link: "Open it" };
  if (standing === "failed" || standing === "stopped")
    return { words: "Your last draft did not finish.", link: "See why" };
  return {
    words: ready ? "Slide to confirm first." : "Settle the items above first.",
    link: null,
  };
}

/** What the slide confirmed: the reads the page showed, and the latest run it knew of. */
interface Lock {
  shown: readonly unknown[];
  runId: string | null | undefined;
}

/**
 * A lock holds while the page shows what the slide confirmed. A read keeps its data's identity
 * until what it holds changes, so any change to what is shown releases the lock; so does a run
 * started anywhere since, even one already ended.
 */
function holds(
  lock: Lock | null,
  shown: readonly unknown[],
  runId: string | null | undefined,
): boolean {
  return (
    lock !== null &&
    lock.runId === runId &&
    lock.shown.length === shown.length &&
    lock.shown.every((read, i) => read === shown[i])
  );
}

/**
 * The lock the slide sets and the start Generate sends, as one status. A lock with a gate shut is
 * no lock. A start, landed or refused, releases it: the next draft takes a slide of its own. The
 * start leaves its queued run as the latest, so the status is generating from its answer on.
 */
function useGenerateStart(
  taskId: string,
  ready: boolean,
  openItemsAddress: string,
  shown: readonly unknown[],
) {
  const navigate = useNavigate();
  const start = useStartRun(taskId);
  const latest = useLatestRunStanding(taskId);
  const runId = useLatestRunId(taskId).data;
  // The terms as the review showed them, for the run's lock to count the edits made from here.
  const shownTerms = use(TerminologyContext)?.review.shown ?? null;
  const [attempt, setAttempt] = useState(0);
  const [lock, setLock] = useState<Lock | null>(null);
  const toRun = () => void navigate(subStepPath(taskId, "generate", "ready"));
  const begin = () => {
    start.mutate(
      { openItemsAddress, terms: shownTerms },
      {
        onSettled: () => {
          setLock(null);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === "DRAFT_RUN_ACTIVE") {
            toRun();
            return;
          }
          setAttempt((n) => n + 1);
        },
      },
    );
  };
  const status: StartStatus = isRunActive(latest.data)
    ? "generating"
    : start.isPending
      ? "starting"
      : ready && holds(lock, shown, runId)
        ? "locked"
        : "open";
  return {
    status,
    standing: latest.data,
    // Held until the first read of the latest run answers: a run started in another window is
    // only known then. A read that keeps failing releases it; the server refuses.
    reading: latest.isPending,
    refusal: start.isError && status !== "generating" ? start.error : null,
    attempt,
    lock: () => {
      setLock({ shown, runId });
    },
    begin,
    toRun,
  };
}

/**
 * The slide locks the content in, and Generate starts the run (owner, 15/10/2026). The lock is
 * the page's: it holds what the page showed when the slide confirmed it, so a change to any of
 * it, or a run started elsewhere, releases it and the slide starts over. Generate sends the
 * open items' address, and the server
 * checks every gate on the state it freezes and queues the run. From that moment, and whenever
 * the page opens on a run still queued or running, the page says the draft is being generated
 * and holds the slide and Generate, so nothing invites a second run; the server refuses one
 * anyway. A refusal puts the slide back and says why; a run already under way is the one to go
 * to.
 */
function StartControls({
  taskId,
  ready,
  openItemsAddress,
  shown,
}: {
  taskId: string;
  ready: boolean;
  openItemsAddress: string;
  shown: readonly unknown[];
}) {
  const { status, standing, reading, refusal, attempt, lock, begin, toRun } =
    useGenerateStart(taskId, ready, openItemsAddress, shown);
  const said = standingWords(standing, status, ready);
  return (
    <>
      <SlideToConfirm
        key={attempt}
        className="mx-auto mt-5"
        label="Slide to confirm the content for this draft"
        doneLabel={
          status === "generating"
            ? "Draft being generated"
            : "Content confirmed"
        }
        confirmed={status !== "open"}
        held={
          !ready || reading || status === "starting" || status === "generating"
        }
        onConfirm={lock}
      />
      {refusal !== null && (
        <Note tone="destructive" role="alert" className="mt-3 text-center">
          {refusalDetail(refusal, "The draft could not be started. Try again.")}
        </Note>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        <GenerateButton status={status} onGenerate={begin} />
        <span role="status" className="text-xs text-muted-foreground">
          {said.words}
        </span>
        {said.link !== null && (
          <Button type="button" variant="link" onClick={toRun}>
            {said.link}
          </Button>
        )}
      </div>
    </>
  );
}

/**
 * The last acts on the page: the attorney confirms what they read with the
 * slide, then generates. The slide is held while a gate is shut. Questions still open do
 * not hold it: they go into the draft as they are, and the attorney is told so
 * before the slide.
 */
export function GenerateConfirm({
  taskId,
  ready,
  gates,
  openItemsAddress,
  shown,
}: {
  taskId: string;
  ready: boolean;
  gates: ReviewGate[];
  openItemsAddress: string;
  /** The reads the page shows; the slide's lock holds only while each is as it was. */
  shown: readonly unknown[];
}) {
  const shut = gates.filter((gate) => !gate.met);
  return (
    <Panel as="section" aria-label="Generate" className="mt-6">
      <h2 className="text-lg font-extrabold tracking-tight">
        Please confirm that all content on this screen is correct before
        generating the draft.
      </h2>
      <p className="mt-1.5 max-w-[62ch] text-[13px] text-muted-foreground">
        The draft uses the content on this screen as it is now. A change you
        make after you generate goes into a draft only when you generate again.
      </p>
      {shut.length > 0 && <StillToDo taskId={taskId} gates={shut} />}
      <StartControls
        taskId={taskId}
        ready={ready}
        openItemsAddress={openItemsAddress}
        shown={shown}
      />
    </Panel>
  );
}
